import { getERPClient } from "./client";

/**
 * DocField schema matching ERPNext DocType field structure
 */
export type DocFieldSchema = {
  fieldname: string;
  label: string;
  fieldtype: string; // "Data", "Link", "Select", "Check", "Int", "Currency", "Date", etc.
  options?: string; // For "Select" options or "Link" DocType
  reqd?: 0 | 1;
  hidden?: 0 | 1;
  in_list_view?: 0 | 1;
  read_only?: 0 | 1;
  depends_on?: string;
  mandatory_depends_on?: string; // Dynamic required field condition
  read_only_depends_on?: string; // Dynamic read-only condition
  default?: string | number;
  max_file_size?: number | string; // For attachment fields
  description?: string; // Field help text / description
  fetch_from?: string; // Auto-populate from linked doctype field (e.g. "item_code.stock_uom")
  set_only_once?: number; // 1 = locked after first save
  allow_on_submit?: number; // 1 = editable on submitted docs
  permlevel?: number; // 0-9, fields with permlevel > 0 require elevated permissions
  link_title_field?: string; // For Link fields: linked DocType's title_field when show_title_field_in_link is set
  link_filters?: string; // For Link fields: Frappe-native JSON filter string applied to the link picker
};

/**
 * DocType-level permissions for current user
 */
export type DocTypePermissions = {
  read: boolean;
  write: boolean;
  submit?: boolean;
  cancel?: boolean;
  amend?: boolean;
};

/**
 * DocType schema structure
 */
export type DocTypeSchema = {
  name: string;
  fields: DocFieldSchema[];
  title_field?: string;
  search_fields?: string;
  istable?: 0 | 1;
  is_submittable?: 0 | 1;
  autoname?: string;
  allow_rename?: 0 | 1;
  permissions?: DocTypePermissions;
};

/**
 * Normalize ERPNext meta response to our internal schema format
 */
function normalizeMetaResponse(meta: any): DocTypeSchema {
  // Handle different response formats:
  // 1. Direct meta object: { name: "...", fields: [...] }
  // 2. Wrapped in data array: { data: [{ name: "...", fields: [...] }] }
  // 3. Wrapped in docs array: { docs: [{ name: "...", fields: [...] }] }
  // 4. Method response: { message: { name: "...", fields: [...] } }
  let docType = meta;
  
  if (meta.data && Array.isArray(meta.data) && meta.data.length > 0) {
    docType = meta.data[0];
  } else if (meta.docs && Array.isArray(meta.docs) && meta.docs.length > 0) {
    docType = meta.docs[0];
  } else if (meta.message) {
    docType = meta.message;
  }

  return {
    name: docType.name || docType.doctype || "",
    fields: (docType.fields || []).map((f: any) => ({
      fieldname: f.fieldname || "",
      label: f.label || (["Section Break", "Column Break", "Tab Break"].includes(f.fieldtype) ? "" : f.fieldname) || "",
      fieldtype: f.fieldtype || "Data",
      options: f.options,
      reqd: f.reqd || 0,
      hidden: f.hidden || 0,
      in_list_view: f.in_list_view || 0,
      read_only: f.read_only || 0,
      depends_on: f.depends_on,
      mandatory_depends_on: f.mandatory_depends_on,
      read_only_depends_on: f.read_only_depends_on,
      default: f.default,
      max_file_size: f.max_file_size,
      description: f.description,
      fetch_from: f.fetch_from,
      set_only_once: f.set_only_once || 0,
      allow_on_submit: f.allow_on_submit || 0,
      permlevel: f.permlevel || 0,
      link_title_field: f.link_title_field || undefined,
      link_filters: f.link_filters || undefined,
    })),
    title_field: docType.title_field,
    search_fields: docType.search_fields,
    istable: docType.istable || 0,
    is_submittable: docType.is_submittable || 0,
    autoname: docType.autoname,
    allow_rename: docType.allow_rename || 0,
    permissions: docType.permissions || undefined,
  };
}

/**
 * Get DocType metadata using meta bundle endpoint (preferred) or fallback to getdoctype method.
 * Uses the meta bundle endpoint which includes doctype-level permissions.
 * Response is non-cacheable (user-dependent) and safe headers are set server-side.
 * 
 * @param doctype - The DocType name (e.g., "Item", "Customer")
 * @returns Normalized DocType schema
 */
async function getDocTypeSchema(doctype: string): Promise<DocTypeSchema> {
  const client = getERPClient();

  try {
    // Try meta bundle endpoint first (includes permissions)
    try {
      const meta = await client.call("zaviago_backend.api.meta_bundle.get_meta_bundle", {
        doctype,
        include_permissions: true,
      });
      const normalized = normalizeMetaResponse(meta);
      // Validate we got fields
      if (normalized.fields && normalized.fields.length > 0) {
        return normalized;
      }
    } catch (bundleError) {
      // Fallback to legacy getdoctype method if meta bundle fails
      console.warn(
        `Meta bundle endpoint failed for ${doctype}, falling back to getdoctype:`,
        bundleError
      );
    }
    
    // Fallback to legacy method
    // Note: getdoctype returns { docs: [...] } but tenantServer.call returns data.message
    // which is undefined for this endpoint. We need to make a raw fetch for control_plane mode.
    const { useAuthStore } = await import("@/lib/store/authStore");
    const authMode = useAuthStore.getState().authMode;

    let meta: any;
    if (authMode === 'control_plane') {
      // Direct fetch to get full response including docs array
      const { tenantServerUrl, cpToken, currentTenant } = useAuthStore.getState();
      const response = await fetch(`${tenantServerUrl}/api/method/frappe.desk.form.load.getdoctype`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${cpToken}`,
          'X-Tenant-ID': currentTenant || '',
        },
        credentials: 'omit',
        body: JSON.stringify({ doctype }),
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch doctype metadata: ${response.status}`);
      }
      meta = await response.json();
    } else {
      meta = await client.call("frappe.desk.form.load.getdoctype", {
        doctype,
      });
    }

    const normalized = normalizeMetaResponse(meta);
    // Validate we got fields
    if (normalized.fields && normalized.fields.length > 0) {
      return normalized;
    }
    throw new Error(`DocType metadata returned empty fields for ${doctype}`);
  } catch (error) {
    throw new Error(
      `Failed to load DocType metadata for ${doctype}: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

export const meta = {
  getDocTypeSchema,
};

